import { useRef, useState, type JSX } from "react";
import {
  useFloating,
  useHover,
  useInteractions,
  useDismiss,
  useRole,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  FloatingPortal,
  type Placement,
} from "@floating-ui/react";
import clsx from "clsx";

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: Placement;
  boundarySelector?: string;
  delay?: number;
  className?: string;
  as?: React.ElementType;
}

const ARROW_SIZE = 10;

function findBoundary(reference: Element, selector: string): Element | undefined {
  return reference.closest(selector) ?? undefined;
}

export function Tooltip(props: TooltipProps): JSX.Element {
  const {
    content,
    children,
    placement = "top",
    boundarySelector = ".container-wrapper",
    delay = 150,
    className,
    as: Component = "div",
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);

  const {
    refs,
    floatingStyles,
    context,
    middlewareData,
    placement: resolvedPlacement,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(ARROW_SIZE + 4),
      flip((state) => ({
        boundary: findBoundary(state.elements.reference as Element, boundarySelector),
      })),
      shift((state) => ({
        boundary: findBoundary(state.elements.reference as Element, boundarySelector),
        padding: 8,
      })),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { delay: { open: delay, close: 0 } });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  const basePlacement = resolvedPlacement.split("-")[0];
  const staticSide = {
    top: "bottom",
    right: "left",
    bottom: "top",
    left: "right",
  }[basePlacement] as string;

  const arrowX = middlewareData.arrow?.x;
  const arrowY = middlewareData.arrow?.y;

  if (!content) return <>{children}</>;

  return (
    <>
      <Component ref={refs.setReference} {...getReferenceProps()} className={clsx(className)}>
        {children}
      </Component>
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[9999] max-w-xs rounded-lg bg-white p-4 text-sm shadow-[0_4px_20px_rgba(0,0,0,0.15)]"
          >
            <div className="text-eerie-black font-family-third text-sm leading-relaxed">{content}</div>
            <div
              ref={arrowRef}
              className="absolute bg-white"
              style={{
                width: ARROW_SIZE,
                height: ARROW_SIZE,
                transform: "rotate(45deg)",
                ...(arrowX != null ? { left: arrowX } : {}),
                ...(arrowY != null ? { top: arrowY } : {}),
                [staticSide]: -ARROW_SIZE / 2,
              }}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
